import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';

export interface HudFighter {
  name: string;
  /** Under Curse: it fights slowed, and the panel says so beside its name. */
  cursed?: boolean;
  hp: number;
  maxHp: number;
  color: string;
  /**
   * How far through the weapon's recovery this fighter is, 0 (just struck) … 1 (ready). Characters
   * recover at their own pace — a dragon's bolt takes nearly three times as long to gather as an
   * arrow — so the player has to be able to see it rather than guess why the button does nothing.
   */
  ready: number;
}

export interface HudState {
  me: HudFighter;
  enemy: HudFighter;
  /** Seconds fought. */
  time: number;
  /** Sudden death warning or the outcome; nothing while the fight simply runs. */
  message: string | null;
  /** 0 … 1, fades after the local player was hit. */
  hitFlash: number;
}

export interface ArenaHud {
  /**
   * Stands the plaque at `position`, `width` metres across, its face turned to `viewer`. It is the
   * headset's HUD, a thing standing by the arena rather than a layer stuck to a face; on a screen
   * the battle panel shows the same, and the plaque stays down.
   */
  place(position: Vector3, width: number, viewer: Vector3 | null): void;
  hide(): void;
  /** Whether the plaque stands; there is no point drawing into it while it does not. */
  readonly shown: boolean;
  update(state: HudState): void;
  dispose(): void;
}

const WIDTH = 920;
const HEIGHT = 260;
/** The plaque's own size in metres, before `place` scales it; the texture keeps its proportions. */
const PLANE_WIDTH = 0.46;
const PLANE_HEIGHT = (PLANE_WIDTH * HEIGHT) / WIDTH;
const BAR_WIDTH = 380;
const FRAME_BACKGROUND = 'rgba(10, 14, 28, 0.72)';

/**
 * The in-world battle HUD: both hit-point bars, the clock and a red wash when the player takes a
 * hit. It stands beside the arena in world space rather than riding a camera.
 *
 * The strip that pointed at the enemy is gone with the first-person camera that needed it: under
 * a fixed bird's-eye view the player can simply see where the enemy is.
 */
export function createArenaHud(scene: Scene): ArenaHud {
  const plane: Mesh = MeshBuilder.CreatePlane(
    'arenaHud',
    { width: PLANE_WIDTH, height: PLANE_HEIGHT },
    scene,
  );
  plane.isPickable = false;
  // Always drawn on top: it must never clip into a wall the player stands next to.
  plane.renderingGroupId = 1;
  plane.setEnabled(false);

  const texture = AdvancedDynamicTexture.CreateForMesh(plane, WIDTH, HEIGHT, false);
  const frame = new Rectangle('hudFrame');
  frame.background = FRAME_BACKGROUND;
  frame.color = 'rgba(255, 255, 255, 0.25)';
  frame.thickness = 3;
  frame.cornerRadius = 40;
  texture.addControl(frame);

  const label = (name: string, left: number, align: number, color: string, size: number) => {
    const text = new TextBlock(name, '');
    text.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    text.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    text.textHorizontalAlignment = align;
    text.left = `${left}px`;
    text.top = '22px';
    text.width = `${BAR_WIDTH}px`;
    text.height = '50px';
    text.fontSize = size;
    text.fontWeight = '700';
    text.color = color;
    frame.addControl(text);
    return text;
  };
  const bar = (name: string, left: number, fromRight: boolean) => {
    const track = new Rectangle(`${name}Track`);
    track.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    track.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    track.left = `${left}px`;
    track.top = '84px';
    track.width = `${BAR_WIDTH}px`;
    track.height = '34px';
    track.thickness = 0;
    track.cornerRadius = 10;
    track.background = 'rgba(255, 255, 255, 0.12)';
    frame.addControl(track);
    const fill = new Rectangle(`${name}Fill`);
    fill.horizontalAlignment = fromRight
      ? Control.HORIZONTAL_ALIGNMENT_RIGHT
      : Control.HORIZONTAL_ALIGNMENT_LEFT;
    fill.thickness = 0;
    fill.cornerRadius = 10;
    track.addControl(fill);
    return fill;
  };

  /** A thin strip under a hit-point bar: how close that fighter's weapon is to being ready. */
  const readyBar = (name: string, left: number, fromRight: boolean) => {
    const track = new Rectangle(`${name}ReadyTrack`);
    track.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    track.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    track.left = `${left}px`;
    track.top = '124px';
    track.width = `${BAR_WIDTH}px`;
    track.height = '12px';
    track.thickness = 0;
    track.cornerRadius = 6;
    track.background = 'rgba(255, 255, 255, 0.1)';
    frame.addControl(track);
    const fill = new Rectangle(`${name}ReadyFill`);
    fill.horizontalAlignment = fromRight
      ? Control.HORIZONTAL_ALIGNMENT_RIGHT
      : Control.HORIZONTAL_ALIGNMENT_LEFT;
    fill.thickness = 0;
    fill.cornerRadius = 6;
    track.addControl(fill);
    return fill;
  };

  const myName = label('myName', 30, Control.HORIZONTAL_ALIGNMENT_LEFT, '#e8ecf5', 40);
  const enemyName = label(
    'enemyName',
    WIDTH - 30 - BAR_WIDTH,
    Control.HORIZONTAL_ALIGNMENT_RIGHT,
    '#e8ecf5',
    40,
  );
  const myBar = bar('my', 30, false);
  const enemyBar = bar('enemy', WIDTH - 30 - BAR_WIDTH, true);
  const myReady = readyBar('my', 30, false);
  const enemyReady = readyBar('enemy', WIDTH - 30 - BAR_WIDTH, true);

  const clock = new TextBlock('clock', '0:00');
  clock.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
  clock.top = '26px';
  clock.width = '120px';
  clock.height = '50px';
  clock.fontSize = 44;
  clock.fontWeight = '700';
  clock.color = '#f2d27a';
  frame.addControl(clock);

  const warning = new TextBlock('warning', '');
  warning.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
  warning.top = '154px'; // clear of the recovery strips
  warning.height = '40px';
  warning.fontSize = 30;
  warning.color = '#f5b041';
  warning.isVisible = false;
  frame.addControl(warning);

  const hpColor = (f: HudFighter) => (f.hp / f.maxHp > 0.35 ? f.color : '#ec7063');
  // Dim while the weapon is gathering, bright the moment it can be used.
  const readyColor = (ready: number) => (ready >= 1 ? '#f2d27a' : 'rgba(242, 210, 122, 0.45)');

  return {
    place(position, width, viewer) {
      plane.parent = null;
      plane.position.copyFrom(position);
      plane.scaling.setAll(width / PLANE_WIDTH);
      if (viewer) turnFaceTo(plane, viewer);
      plane.setEnabled(true);
    },
    hide() {
      plane.setEnabled(false);
    },
    get shown() {
      return plane.isEnabled(false);
    },
    update(state) {
      myName.text = hudName(state.me);
      enemyName.text = hudName(state.enemy);
      myBar.width = `${Math.round((BAR_WIDTH * state.me.hp) / state.me.maxHp)}px`;
      myBar.background = hpColor(state.me);
      myReady.width = `${Math.round(BAR_WIDTH * clamp01(state.me.ready))}px`;
      myReady.background = readyColor(state.me.ready);
      enemyBar.width = `${Math.round((BAR_WIDTH * state.enemy.hp) / state.enemy.maxHp)}px`;
      enemyBar.background = hpColor(state.enemy);
      enemyReady.width = `${Math.round(BAR_WIDTH * clamp01(state.enemy.ready))}px`;
      enemyReady.background = readyColor(state.enemy.ready);
      const seconds = Math.floor(state.time);
      clock.text = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
      warning.text = state.message ?? '';
      warning.isVisible = state.message !== null;
      frame.background =
        state.hitFlash > 0
          ? `rgba(${Math.round(10 + 170 * state.hitFlash)}, 14, 28, 0.72)`
          : FRAME_BACKGROUND;
    },
    dispose() {
      texture.dispose();
      plane.material?.dispose();
      plane.dispose();
    },
  };
}

/**
 * Turns a plane's face to a viewer. A plane shows its face along -Z and `lookAt` turns +Z to its
 * target, so it looks through itself, away from the viewer: the viewer reads its face, not its
 * mirror image.
 */
export function turnFaceTo(plane: TransformNode, viewer: Vector3): void {
  plane.lookAt(plane.position.scale(2).subtractInPlace(viewer));
}

/** A cursed fighter fights slowed (D-31); its name says so, since nothing else would. */
function hudName(f: HudFighter): string {
  return f.cursed ? `${f.name} · cursed` : f.name;
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1;
}
